<template>
    <v-row justify="center">
        <v-dialog
            v-model="isOpen"
            @click:outside="closeWhenNoChanges()"
            @keydown.esc="closeWhenNoChanges()"
            persistent
            max-width="600px"
        >
            <v-card>
                <v-form>
                    <v-card-title>
                        <span class="headline">{{
                            formData.id
                                ? $t('createOrUpdateVacation.editTitle')
                                : $t('createOrUpdateVacation.createTitle')
                        }}</span>
                    </v-card-title>
                    <v-card-text>
                        <v-container>
                            <v-row>
                                <v-col v-if="isAdmin" cols="12">
                                    <v-autocomplete
                                        v-model="employee"
                                        :items="employees"
                                        :loading="isLoading"
                                        :label="
                                            $t(
                                                'createOrUpdateVacation.forms.employee'
                                            )
                                        "
                                        :error-messages="userIdErrors"
                                        hide-no-data
                                        hide-selected
                                        placeholder="Start typing to Search"
                                        item-text="fullName"
                                        item-value="id"
                                        return-object
                                        autofocus
                                        clearable
                                        @update:search-input="
                                            onUpdateEmployeeSearch
                                        "
                                        @change="onChangeEmployee"
                                        @input="$v.formData.userId.$touch()"
                                        @blur="$v.formData.userId.$touch()"
                                        @keyup="clearServerErrors('userId')"
                                    />
                                </v-col>
                                <v-col cols="12" sm="6">
                                    <v-menu
                                        v-model="isStartDatePickerVisible"
                                        :close-on-content-click="false"
                                        transition="scale-transition"
                                        offset-y
                                        min-width="290px"
                                    >
                                        <template
                                            v-slot:activator="{ on, attrs }"
                                        >
                                            <v-text-field
                                                :label="
                                                    $t(
                                                        'createOrUpdateVacation.forms.startDate'
                                                    )
                                                "
                                                prepend-icon="mdi-calendar"
                                                readonly
                                                :value="
                                                    formData.startDate
                                                        | dateFormatFromSettings
                                                "
                                                :error-messages="
                                                    startDateErrors
                                                "
                                                v-bind="attrs"
                                                v-on="on"
                                            />
                                        </template>
                                        <v-date-picker
                                            v-model="formData.startDate"
                                            no-title
                                            scrollable
                                            :picker-date.sync="
                                                pickerYearAndMonth
                                            "
                                            :allowed-dates="allowedDates"
                                            @input="onStartDatePickerInput"
                                        />
                                    </v-menu>
                                </v-col>
                                <v-col cols="12" sm="6">
                                    <v-menu
                                        v-model="isEndDatePickerVisible"
                                        :close-on-content-click="false"
                                        transition="scale-transition"
                                        offset-y
                                        min-width="290px"
                                    >
                                        <template
                                            v-slot:activator="{ on, attrs }"
                                        >
                                            <v-text-field
                                                :label="
                                                    $t(
                                                        'createOrUpdateVacation.forms.endDate'
                                                    )
                                                "
                                                prepend-icon="mdi-calendar"
                                                readonly
                                                :value="
                                                    formData.endDate
                                                        | dateFormatFromSettings
                                                "
                                                :error-messages="endDateErrors"
                                                v-bind="attrs"
                                                v-on="on"
                                            />
                                        </template>
                                        <v-date-picker
                                            v-model="formData.endDate"
                                            no-title
                                            scrollable
                                            :picker-date.sync="
                                                pickerYearAndMonth
                                            "
                                            :allowed-dates="allowedDates"
                                            @input="onEndDatePickerInput"
                                        />
                                    </v-menu>
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer />
                        <v-btn color="red darken-1" text @click="onClose">{{
                            $t('createOrUpdateVacation.forms.close')
                        }}</v-btn>
                        <v-btn color="blue darken-1" text @click="onSave">{{
                            $t('createOrUpdateVacation.forms.save')
                        }}</v-btn>
                    </v-card-actions>
                </v-form>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
import dayjs from 'dayjs';
import { mapActions, mapGetters } from 'vuex';
import calculateHolidayDates from '@/services/calculateHolidayDates';
import createOrEditValidator from '@/validators/vacations/createOrEditValidator.mixin';

export default {
    name: 'createOrUpdateVacation',
    props: {
        isOpen: Boolean,
        vacation: {
            type: Object,
            required: false,
            default: () => ({})
        }
    },
    mixins: [createOrEditValidator],
    data() {
        return {
            employee: {},
            searchedEmployees: [],
            defaultFormData: {
                userId: '',
                startDate: dayjs().format('YYYY-MM-DD'),
                endDate: dayjs().format('YYYY-MM-DD')
            },
            formData: {},
            formDataCopy: {},
            isLoading: false,
            errorMessage: '',
            isStartDatePickerVisible: false,
            isEndDatePickerVisible: false,
            pickerYearAndMonth: dayjs().format('YYYY-MM'),
            holidayDates: [],
            holidays: []
        };
    },
    computed: {
        ...mapGetters({
            isAdmin: 'auth/isAdmin',
            loggedUser: 'auth/getLoggedUser'
        }),

        employees() {
            return this.searchedEmployees.map(e => {
                return {
                    id: e.id,
                    fullName: e.firstName + ' ' + e.lastName
                };
            });
        }
    },
    watch: {
        vacation() {
            this.setDefaultData();

            if (!this.vacation) {
                return;
            }

            this.formData = {
                ...this.vacation
            };

            this.formDataCopy = {
                ...this.formData
            };

            const { user } = this.vacation;

            if (user && this.isAdmin) {
                this.employee = {
                    id: user.id,
                    fullName: user.firstName + ' ' + user.lastName
                };
                this.searchedEmployees.push(user);
            }
        },

        pickerYearAndMonth: {
            immediate: true,
            async handler(newYearMonth, oldYearMonth) {
                const {
                    holidays,
                    holidayDates
                } = await calculateHolidayDates.handle(
                    newYearMonth,
                    oldYearMonth,
                    this.holidays
                );

                this.holidays = holidays;
                this.holidayDates = holidayDates;
            }
        },

        async isOpen(val) {
            if (!val) {
                return;
            }

            const {
                holidays,
                holidayDates
            } = await calculateHolidayDates.handle(this.pickerYearAndMonth);

            this.holidays = holidays;
            this.holidayDates = holidayDates;

            if (!this.vacation.id) {
                let initialStartDate = this.defaultFormData.startDate;

                while (!this.allowedDates(initialStartDate)) {
                    initialStartDate = dayjs(initialStartDate)
                        .add(1, 'day')
                        .format('YYYY-MM-DD');

                    this.formData.startDate = initialStartDate;
                    this.formData.endDate = initialStartDate;
                }
            }
        },

        'formData.startDate': {
            handler(newDate) {
                this.formData.endDate = newDate;
            }
        }
    },
    created() {
        this.setDefaultFormData();
    },
    methods: {
        ...mapActions({
            searchUsers: 'users/search',
            fetchHolidaysForYear: 'holidays/fetchForYear',
            saveVacation: 'vacations/save'
        }),

        setDefaultFormData() {
            this.formData = {
                ...this.defaultFormData,
                isConfirmed: this.isAdmin,
                userId: !this.isAdmin && this.loggedUser.id
            };

            this.formDataCopy = { ...this.formData };
        },
        setDefaultData() {
            this.setDefaultFormData();
            this.employee = {};
            this.searchedEmployees = [];
            this.errorMessage = '';
        },
        async onUpdateEmployeeSearch(query) {
            if (!query || this.isLoading) return;
            if (this.employee && this.employee.fullName === query) return;

            this.isLoading = true;
            this.searchedEmployees = await this.searchUsers(query);
            this.isLoading = false;
        },
        onChangeEmployee(employee) {
            this.formData.userId = employee
                ? employee.id
                : this.defaultFormData.userId;
        },
        async onSave() {
            try {
                this.$v.$touch();

                if (!this.isValid) {
                    return;
                }

                await this.saveVacation(this.formData);

                this.$notify({
                    group: 'notify',
                    type: 'success',
                    title: this.formData.id
                        ? this.$t('createOrUpdateVacation.editTitle')
                        : this.$t('createOrUpdateVacation.createTitle'),
                    text: this.$t('createOrUpdateVacation.saveSuccess')
                });

                this.onClose();
            } catch (error) {
                this.errorMessage = '';

                if (this.checkForServerFormErrors(error)) {
                    this.$notify({
                        group: 'notify',
                        type: 'error',
                        title: this.formData.id
                            ? this.$t('createOrUpdateVacation.editTitle')
                            : this.$t('createOrUpdateVacation.createTitle'),
                        text: this.$t('createOrUpdateVacation.checkFormErrors')
                    });
                }
            }
        },
        async onClose() {
            setTimeout(this.setDefaultData, 100);
            this.$v.$reset();
            this.$emit('close');
        },
        onStartDatePickerInput() {
            this.$v.formData.startDate.$touch();
            this.clearServerErrors('startDate');
            this.isStartDatePickerVisible = false;
        },
        onEndDatePickerInput() {
            this.$v.formData.endDate.$touch();
            this.clearServerErrors('endDate');
            this.isEndDatePickerVisible = false;
        },
        allowedDates(pickerDate) {
            const day = dayjs(pickerDate).day();
            const dayOfMonth = dayjs(pickerDate).date();

            return (
                day !== 0 &&
                day !== 6 &&
                !this.holidayDates.includes(dayOfMonth)
            );
        },
        closeWhenNoChanges() {
            const currentData = Object.entries(this.formData).toString();
            const previousData = Object.entries(this.formDataCopy).toString();

            if (previousData !== currentData) {
                return;
            }

            this.onClose();
        }
    }
};
</script>
